import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../utils/apiClient';
import { RefreshCw, CheckCircle, AlertTriangle, Play, Settings, Clock, Activity } from 'lucide-react';
import { message } from 'antd';

const PayrollStatusCard = ({ onNavigate, canTrigger = true, canManageSettings = true }) => {
    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isTriggering, setIsTriggering] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const data = await apiClient('PayrollSettings/status');
            setStatus(data);
        } catch (err) {
            console.error('Payroll status fetch error:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // Akıllı Polling: Hesaplama çalışırken 4 saniyede bir, boşta iken 60 saniyede bir (sadece sekme görünürken)
    useEffect(() => {
        const intervalMs = status?.isRunning ? 4000 : 60000;
        const timer = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden' && !status?.isRunning) {
                return;
            }
            fetchStatus();
        }, intervalMs);

        return () => clearInterval(timer);
    }, [status?.isRunning, fetchStatus]);

    const handleTriggerNow = async () => {
        if (!canTrigger || status?.isRunning || isTriggering) return;
        setIsTriggering(true);
        try {
            const res = await apiClient('PayrollSettings/trigger-now', { method: 'POST' });
            message.success(res.message || 'Puantaj hesaplama işlemi başlatıldı.');
            fetchStatus();
        } catch (err) {
            message.error(err.message || 'Hesaplama başlatılamadı.');
        } finally {
            setIsTriggering(false);
        }
    };

    const handleGoToSettings = () => {
        if (onNavigate) {
            onNavigate('payroll_calc_settings');
        }
    };

    if (isLoading && !status) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700/80 p-4 mb-6 animate-pulse flex items-center justify-between">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
            </div>
        );
    }

    if (!status) return null;

    const formatEta = (seconds) => {
        if (seconds === null || seconds === undefined || seconds <= 0) return 'Hesaplanıyor...';
        if (seconds < 60) return `~${seconds} sn`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `~${mins} dk ${secs > 0 ? `${secs} sn` : ''}`;
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700/80 p-4 sm:p-5 mb-6 transition-all duration-300 relative overflow-hidden">
            {/* Arka Plan Efekti (Çalışıyorsa) */}
            {status.isRunning && (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-indigo-500/10 to-purple-500/5 dark:from-blue-500/10 dark:via-indigo-500/20 dark:to-purple-500/10 animate-pulse pointer-events-none" />
            )}

            <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                {/* Sol: Başlık ve Durum Rozeti */}
                <div className="flex items-start sm:items-center space-x-3.5">
                    <div className={`p-3 rounded-xl flex-shrink-0 ${
                        status.isRunning
                            ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400'
                            : status.lastStatus === 'Failed'
                                ? 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400'
                                : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400'
                    }`}>
                        {status.isRunning ? (
                            <RefreshCw className="w-6 h-6 animate-spin" />
                        ) : status.lastStatus === 'Failed' ? (
                            <AlertTriangle className="w-6 h-6" />
                        ) : (
                            <Activity className="w-6 h-6" />
                        )}
                    </div>

                    <div>
                        <div className="flex items-center flex-wrap gap-2">
                            <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">
                                Puantaj Hesaplama Motoru
                            </h3>
                            {status.isRunning ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600 dark:bg-blue-400 mr-1.5 animate-ping" />
                                    Hesaplanıyor...
                                </span>
                            ) : status.isEnabled ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200">
                                    Otomatik: Aktif (Son {status.lookbackDays} Gün)
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                    Otomatik: Pasif
                                </span>
                            )}
                        </div>

                        {/* Zaman Bilgileri */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center">
                                <CheckCircle className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                                Son Tamamlanma: <strong className="ml-1 text-gray-700 dark:text-gray-200">{status.lastCompletedAtStr || 'Henüz Yok'}</strong>
                            </span>
                            {status.isEnabled && status.nextPlannedAtStr && status.nextPlannedAtStr !== '-' && (
                                <span className="flex items-center">
                                    <Clock className="w-3.5 h-3.5 mr-1 text-indigo-500" />
                                    Sonraki Planlı: <strong className="ml-1 text-gray-700 dark:text-gray-200">{status.nextPlannedAtStr}</strong>
                                </span>
                            )}
                        </div>

                        {status.lastStatus === 'Failed' && status.errorMessage && !status.isRunning && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400 font-medium">
                                Son işlem hatası: {status.errorMessage}
                            </p>
                        )}
                    </div>
                </div>

                {/* Orta: Eğer Çalışıyorsa İlerleme Çubuğu (Progress Bar) ve ETA */}
                {status.isRunning && (
                    <div className="w-full lg:w-1/3 bg-gray-50 dark:bg-gray-900/60 p-3 rounded-xl border border-blue-100 dark:border-blue-900/40">
                        <div className="flex justify-between items-center text-xs mb-1.5">
                            <span className="font-semibold text-blue-700 dark:text-blue-300">
                                İlerleme: %{status.progressPercent} ({status.processedEmployees} / {status.totalEmployees || '?'} Personel)
                            </span>
                            <span className="text-gray-600 dark:text-gray-400 font-medium">
                                Kalan: <strong className="text-indigo-600 dark:text-indigo-400">{formatEta(status.etaSeconds)}</strong>
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                            <div
                                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2.5 rounded-full transition-all duration-500"
                                style={{ width: `${Math.max(4, status.progressPercent)}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Sağ: Aksiyon Butonları (Yetki Kontrollü) */}
                {(canTrigger || canManageSettings) && (
                    <div className="flex items-center space-x-2.5 w-full lg:w-auto justify-end">
                        {canTrigger && (
                            <button
                                onClick={handleTriggerNow}
                                disabled={status.isRunning || isTriggering}
                                className={`flex items-center px-3.5 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all ${
                                    status.isRunning || isTriggering
                                        ? 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
                                        : 'bg-indigo-600 hover:bg-indigo-700 text-white hover:shadow'
                                }`}
                                title={`Son ${status.lookbackDays} gün için tüm personellerin puantajını şimdi hesapla`}
                            >
                                <Play className="w-3.5 h-3.5 mr-1.5 fill-current" />
                                {status.isRunning ? 'Hesaplama Sürüyor' : 'Şimdi Hesapla'}
                            </button>
                        )}

                        {canManageSettings && onNavigate && (
                            <button
                                onClick={handleGoToSettings}
                                className="flex items-center px-3 py-2 rounded-xl text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 transition-colors"
                                title="Otomatik Hesaplama Ayarları"
                            >
                                <Settings className="w-3.5 h-3.5 mr-1" />
                                Ayarlar
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PayrollStatusCard;
